import { AlertCircle, CheckCircle, ChevronDown, ChevronRight, Clock, FileText, Loader2, Upload, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { UploadModal } from '@/components/documents/UploadModal';

import type { DocumentItem, DocumentStatus } from '@/types/document.types';

interface DocumentChecklistItemProps {
  item: DocumentItem;
  isUploading: boolean;
  isReusing: boolean;
  onUpload: (file: File) => Promise<boolean>;
  onReuse?: () => void;
  onRemove?: () => void;
}

const STATUS_CONFIG: Record<DocumentStatus, { icon: typeof CheckCircle; bgClass: string; textClass: string }> = {
  pending: { icon: FileText, bgClass: 'bg-gray-100', textClass: 'text-gray-500' },
  uploaded: { icon: Clock, bgClass: 'bg-blue-100', textClass: 'text-blue-600' },
  verified: { icon: CheckCircle, bgClass: 'bg-green-100', textClass: 'text-green-600' },
  flagged: { icon: AlertCircle, bgClass: 'bg-yellow-100', textClass: 'text-yellow-600' },
  rejected: { icon: AlertCircle, bgClass: 'bg-red-100', textClass: 'text-red-600' },
};

const ACCEPTED_MIME_TYPES = 'image/*,.pdf,.doc,.docx';
const MAX_SIZE_MB = 10;

export function DocumentChecklistItem({
  item,
  isUploading,
  isReusing,
  onUpload,
  onReuse,
  onRemove,
}: DocumentChecklistItemProps) {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const config = STATUS_CONFIG[item.status];
  const Icon = config.icon;
  const isCompleted = item.status === 'uploaded' || item.status === 'verified';
  const isRejected = item.status === 'rejected';
  const canRemove = item.status === 'uploaded' && item.documentId && onRemove;
  const isPreviouslyUploaded = isCompleted && item.fromPreviousApplication === true;
  const canReuse = !isCompleted && !isRejected && item.reusableDocumentId && onReuse;
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);

  return (
    <>
      <div
        className={`p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 ${
          isCompleted ? 'bg-green-50/30' : isRejected ? 'bg-red-50/30' : ''
        }`}>
        <div className='flex items-start gap-4 flex-1 min-w-0'>
          <div className={`p-2.5 rounded-lg shrink-0 ${config.bgClass} ${config.textClass}`}>
            <Icon size={20} />
          </div>
          <div className='flex-1 min-w-0'>
            <div className='flex items-center gap-2'>
              <h4 className='font-bold text-gray-900 truncate'>{item.name}</h4>
              {!item.isRequired && (
                <span className='text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full shrink-0'>
                  {t('documents.optional')}
                </span>
              )}
            </div>
            <p className='text-sm text-gray-500 mt-0.5'>{item.description}</p>

            {/* Previously uploaded label */}
            {isPreviouslyUploaded && (
              <span className='inline-block text-xs text-blue-500 bg-blue-50 px-2 py-0.5 rounded-full mt-1'>
                {item.status === 'verified'
                  ? t('documents.checklist.verifiedFromPriorApplication')
                  : t('documents.checklist.previouslyUploaded')}
              </span>
            )}

            {/* Show file name if uploaded */}
            {item.fileName && (
              <p className='text-xs text-gray-400 mt-1'>
                {item.fileName}
                {item.uploadedAt && (
                  <span className='ml-2'>
                    {t('documents.uploadedAt', { date: new Date(item.uploadedAt).toLocaleDateString() })}
                  </span>
                )}
              </p>
            )}

            {/* Show rejection reason */}
            {isRejected && item.rejectionReason && (
              <p className='text-xs text-red-600 mt-1'>
                {t('documents.rejectedReason')}: {item.rejectionReason}
              </p>
            )}

            {canReuse && (
              <p className='text-xs text-blue-600 mt-1'>
                {t('documents.checklist.reusableAvailable', {
                  fileName: item.reusableFileName ?? t('documents.checklist.previousDocument'),
                })}
              </p>
            )}

            {isCompleted && isDetailsOpen && (
              <div className='mt-3 rounded-lg border border-gray-200 bg-white p-3 text-xs text-gray-500 space-y-1'>
                <p>
                  <span className='font-medium text-gray-700'>{t('documents.status')}:</span>{' '}
                  {item.status === 'verified' ? t('documents.verified') : t('documents.uploaded')}
                </p>
                {item.fileName && (
                  <p>
                    <span className='font-medium text-gray-700'>{t('documents.fileName')}:</span> {item.fileName}
                  </p>
                )}
                {item.uploadedAt && (
                  <p>
                    <span className='font-medium text-gray-700'>{t('documents.uploaded')}:</span>{' '}
                    {new Date(item.uploadedAt).toLocaleDateString()}
                  </p>
                )}
                {item.documentId && (
                  <p>
                    <span className='font-medium text-gray-700'>{t('documents.documentId')}:</span> {item.documentId}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        <div className='w-full sm:w-auto flex items-center gap-2 justify-end shrink-0'>
          {isUploading || isReusing ? (
            <span className='flex items-center gap-2 text-sm text-primary font-medium px-4 py-2'>
              <Loader2 size={16} className='animate-spin' />
              {isReusing ? t('documents.reusing') : t('documents.uploading')}
            </span>
          ) : isCompleted ? (
            <div className='flex flex-wrap items-center gap-2 justify-end'>
              <span className='text-sm font-medium text-green-600 bg-green-100/50 px-3 py-1.5 rounded-full border border-green-200'>
                {item.status === 'verified' ? t('documents.verified') : t('documents.uploaded')}
              </span>
              <button
                type='button'
                onClick={() => setIsDetailsOpen((open) => !open)}
                className='inline-flex items-center gap-1 px-3 py-1.5 border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition text-sm font-medium'
                aria-expanded={isDetailsOpen}>
                {isDetailsOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                {t('documents.details')}
              </button>
              <button
                type='button'
                onClick={() => setIsModalOpen(true)}
                className='inline-flex items-center gap-2 px-3 py-1.5 border border-primary text-primary rounded-lg hover:bg-blue-50 transition text-sm font-medium'>
                <Upload size={14} />
                {t('documents.replace')}
              </button>
              {canRemove && (
                <button
                  type='button'
                  onClick={onRemove}
                  className='p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition'
                  title={t('documents.remove')}>
                  <X size={14} />
                </button>
              )}
            </div>
          ) : (
            <div className='flex flex-wrap items-center gap-2 justify-end'>
              {canReuse && (
                <button
                  type='button'
                  onClick={onReuse}
                  className='flex items-center gap-2 px-4 py-2 border border-green-600 text-green-700 rounded-lg hover:bg-green-50 transition text-sm font-medium'>
                  <CheckCircle size={16} />
                  {t('documents.reuseDocument')}
                </button>
              )}
              <button
                type='button'
                onClick={() => setIsModalOpen(true)}
                className='flex items-center gap-2 px-4 py-2 border border-primary text-primary rounded-lg hover:bg-blue-50 transition text-sm font-medium'>
                <Upload size={16} />
                {isRejected ? t('documents.reupload') : t('documents.uploadFile')}
              </button>
            </div>
          )}
        </div>
      </div>

      <UploadModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onUpload={onUpload}
        documentType={item.name}
        acceptedMimeTypes={ACCEPTED_MIME_TYPES}
        maxSizeMb={MAX_SIZE_MB}
      />
    </>
  );
}
